<template>
  <BaseDialog
    centered
    contentClass="potential-bidder"
    ref="dialog"
    :show-actions="true"
    :persistent="isDirty"
    @closeModal="closeDialog"
  >
    <template slot="title">{{
      isEdit
        ? $t('page.opportunities.potential-bidder-dialog.edit-title')
        : $t('page.opportunities.potential-bidder-dialog.add-title')
    }}</template>
    <template slot="content">
      <form @submit.prevent="onSubmit" novalidate class="potential-bidder-dialog__form">
        <FormInput
          :label="$t('page.opportunities.potential-bidder-dialog.organization-name')"
          @input="onFormChange"
          has-gray-background
          name="organizationName"
          type="name"
          ref="organizationName"
          v-model="formData.organizationName"
          :error="organizationNameError"
          v-validate="'required'"
          is-required
        />
        <FormInput
          :label="$t('page.opportunities.potential-bidder-dialog.organization-address')"
          @input="onFormChange"
          has-gray-background
          name="organizationAddress"
          ref="organizationAddress"
          v-model="formData.organizationAddress"
        />
        <FormInput
          :label="$t('page.opportunities.potential-bidder-dialog.organization-website')"
          @input="onFormChange"
          has-gray-background
          name="organizationWebsite"
          ref="organizationWebsite"
          v-model="formData.organizationWebsite"
        />
        <div class="potential-bidder-dialog__contact">
          {{ $t('page.opportunities.potential-bidder-dialog.contact-person') }}
        </div>
        <div class="potential-bidder-dialog__two-columns-section">
          <div class="potential-bidder-dialog__column-section">
            <FormInput
              :label="$t('page.opportunities.potential-bidder-dialog.contact-name')"
              @input="onFormChange"
              has-gray-background
              name="contactName"
              ref="contactName"
              v-model="formData.contactName"
            />
            <FormInput
              :label="$t('page.opportunities.potential-bidder-dialog.contact-fax')"
              @input="onFormChange"
              has-gray-background
              name="contactFax"
              ref="contactFax"
              v-model="formData.contactFax"
            />
          </div>
          <div class="potential-bidder-dialog__column-section">
            <FormInput
              :error="contactEmailError"
              :label="$t('page.opportunities.potential-bidder-dialog.contact-email')"
              @input="onFormChange"
              has-gray-background
              name="contactEmail"
              ref="contactEmail"
              type="email"
              v-model="formData.contactEmail"
              v-validate="'email'"
            />
            <FormInput
              :label="$t('page.opportunities.potential-bidder-dialog.contact-phone')"
              @input="onFormChange"
              has-gray-background
              name="contactPhone"
              ref="contactPhone"
              v-model="formData.contactPhone"
            />
          </div>
        </div>
      </form>
    </template>
    <template slot="actions">
      <div class="potential-bidder-dialog__actions">
        <BaseButton @click.native="onCancel" isOutlined>
          {{ $t('actions.cancel') }}
        </BaseButton>
        <BaseButton @click.native="onSubmit" :disabled="hasNoChanges || isFormInvalid">{{
          isEdit ? $t('actions.edit') : $t('actions.add')
        }}</BaseButton>
      </div>
    </template>
  </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/base/BaseDialog';
import FormInput from '@/components/form/FormInput.vue';

import { mapActions } from 'vuex';
import BaseButton from '@/components/base/BaseButton.vue';

export default {
  name: 'PotentialBidderDialog',
  components: {
    BaseButton,
    BaseDialog,
    FormInput,
  },
  props: {
    opportunityId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isDirty: false,
      isEdit: false, // if true then dialog is editing an existing potential bidder, otherwise it is creating a new potential bidder
      potentialBidderId: null,
      formData: {
        organizationName: '',
        organizationAddress: '',
        organizationWebsite: '',
        contactName: '',
        contactEmail: '',
        contactFax: '',
        contactPhone: '',
      },
    };
  },
  inject: ['$validator'],
  computed: {
    isFormInvalid() {
      return !this.formData.organizationName || !!this.contactEmailError;
    },
    hasNoChanges() {
      return !this.isDirty;
    },
    organizationNameError() {
      const error = this.errors.items.find((x) => x.field === 'organizationName');
      return error ? error.msg : undefined;
    },
    contactEmailError() {
      const error = this.errors.items.find((x) => x.field === 'contactEmail');

      return error ? error.msg : undefined;
    },
  },
  methods: {
    ...mapActions('potentialBidders', ['createPotentialBidder', 'editPotentialBidder']),
    openAddPotentialBidderDialog() {
      this.isEdit = false;
      this.initFormData();
      this.openDialog();
    },
    openEditPotentialBidderDialog(potentialBidder) {
      this.isEdit = true;
      this.potentialBidderId = potentialBidder.id;
      this.initFormData(potentialBidder);
      this.openDialog();
    },
    openDialog() {
      this.$refs.dialog.openModal();

      this.$nextTick(() => {
        const refInput = this.$refs.organizationName.$el.querySelector('input');
        if (refInput) {
          this.$nextTick(() => {
            refInput.focus();
          });
        }
      });
    },
    closeDialog() {
      this.isDirty = false;
      this.$refs.dialog.isModalOpen = false;
      this.$validator.reset();
      this.errors.clear();
      this.initFormData();
    },
    onFormChange() {
      this.isDirty = true;
    },
    onCancel() {
      this.closeDialog();
    },
    onSubmit() {
      if (this.isEdit) {
        this.editPotentialBidder({
          opportunityId: this.opportunityId,
          potentialBidderId: this.potentialBidderId,
          potentialBidderData: this.formData,
        })
          .then(() => {
            this.$toast.success(this.$t('page.opportunity-details.potential-bidder-manipulations.edited'));
            this.$emit('action-succeeded');
          })
          .catch((error) => this.$toast.error(error));
      } else {
        this.createPotentialBidder({ opportunityId: this.opportunityId, potentialBidderData: this.formData })
          .then(() => {
            this.$toast.success(this.$t('page.opportunity-details.potential-bidder-manipulations.created'));
            this.$emit('action-succeeded');
          })
          .catch((error) => this.$toast.error(error));
      }
      this.isDirty = false;
      this.closeDialog();
    },
    initFormData(initialData) {
      this.formData = {
        organizationName: initialData && initialData.organization_name ? initialData.organization_name : '',
        organizationAddress: initialData && initialData.address ? initialData.address : '',
        organizationWebsite: initialData && initialData.website ? initialData.website : '',
        contactName:
          initialData && initialData.contact_points && initialData.contact_points.length > 0
            ? initialData.contact_points[0].name
            : '',
        contactEmail:
          initialData && initialData.contact_points && initialData.contact_points.length > 0
            ? initialData.contact_points[0].email
            : '',
        contactFax:
          initialData && initialData.contact_points && initialData.contact_points.length > 0
            ? initialData.contact_points[0].fax
            : '',
        contactPhone:
          initialData && initialData.contact_points && initialData.contact_points.length > 0
            ? initialData.contact_points[0].telephone
            : '',
      };
    },
  },
};
</script>

<style lang="scss">
.base-dialog__potential-bidder {
  height: auto;
  width: 41.25rem;
  position: relative;
  border-radius: 8px;

  .header__title {
    margin: 0 !important;
    padding: 32px;
    width: 100%;
    height: auto !important;
  }

  .base-dialog__content {
    padding: 0 32px !important;
    background: $color-white !important;
  }

  .base-dialog__actions {
    padding: 32px;
  }

  .close-icon__container {
    display: none;
  }
}

.potential-bidder-dialog {
  &__contact {
    color: $color-neutral;
    font-size: $font-size-small;
    padding-bottom: 8px;
    text-transform: uppercase;
  }

  &__two-columns-section {
    display: flex;
    gap: 1.1875rem;
  }

  &__column-section {
    width: 100%;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
  }
}
</style>
